import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Users, Zap, Footprints, Check, X, CalendarDays } from "lucide-react";
import { useApp, IMAGES } from "@/context/AppContext";
import { useNoIndex } from "@/hooks/useNoIndex";
import { NotifyForm } from "@/components/NotifyForm";

const EASE = [0.22, 1, 0.36, 1];
const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div className={className} initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-70px" }} transition={{ duration: 0.9, delay, ease: EASE }}>
    {children}
  </motion.div>
);

const HIGHLIGHTS = [
  { icon: Users, title: "Strictly Limited Cohorts", body: "Capped at 12 women — or 6 couples for Vows & Vessels — so there's deep connection, individual attention, and zero places to hide." },
  { icon: Zap, title: "Daily Application", body: "Daily micro-challenges, video check-ins, and accountability loops keep your spiritual growth active all week — not one hour on Sunday." },
  { icon: Footprints, title: "Embodied Growth", body: "Move beyond sitting in a circle listening to lectures — experience challenges that test and refine your character in real time." },
];

const COHORTS = [
  {
    id: "vessels", track: "vessels", overline: "Applications required", audience: "For Women",
    title: "VESSELS", subtitle: "A Six-Month Formation Journey for Women", img: IMAGES.woman,
    intro: "For women ready to encounter God in ordinary moments. Whether you're navigating motherhood, marriage, career, or unexpected seasons — if you're willing to show up honestly and practice faith, you belong here.",
    fit: ["You want deep, honest accountability", "You're ready to practice faith daily, not just study it", "You crave real community with other women"],
    notFit: ["You're looking for a passive video course", "You want a traditional lecture-style Bible study"],
    moments: [
      { label: "Week three.", body: "You're sitting across from a woman you've never met. She shares a story that somehow feels like yours. Suddenly, you realize you've spent years believing something that was never true." },
      { label: "Month four.", body: "These women know your coffee order. They know what silence means between you. They know when to press in and when to sit with you in it. You're known. Completely." },
      { label: "Six months later.", body: "You show up to motherhood, marriage, your career differently. More present. Less performed. You recognize God's hand in moments that used to feel ordinary. You're gentler with yourself. You're more available to others." },
    ],
    investment: { oneTime: "$1,997 one-time", monthly: "$375/month for 6 months" },
    future: [
      "You'll have friendships deep enough for real truth.",
      "You'll recognize God in moments that used to feel ordinary.",
      "You'll show up to motherhood, marriage, career differently — more present, less performed.",
      "You'll know what it feels like to belong exactly as you are.",
    ],
    cta: "Apply for VESSELS",
  },
  {
    id: "vows", track: "vows-and-vessels", overline: "Applications required", audience: "For Couples",
    title: "Vows & Vessels", subtitle: "A Six-Month Journey for Couples", img: IMAGES.couple,
    intro: "For couples building a Christ-centered life together. Not a fix — a deepening. If you're ready to practice intentional faith, honest conversation, and meaningful connection — this is for you.",
    fit: ["You both want to grow with intention, together", "You're ready for honest conversation and daily practice", "You want couple friendships that actually understand"],
    notFit: ["You're hoping for a quick fix or marriage counseling", "You want a passive, watch-at-home course"],
    lead: [
      "Imagine six months of meals where you're not trying to solve anything. Just building something.",
      "Shared adventures. Honest conversations. Other couples who understand the tension between staying faithful and staying real. Vision nights where you dream about what you're building together. Friendships that anchor you both.",
    ],
    moments: [
      { label: "By month three.", body: "You laugh about things that used to feel heavy. You can be honest in ways you weren't before. You're building something with intention instead of just managing what you have." },
      { label: "By month six.", body: "You won't have fixed your marriage. You'll have remembered why you chose it. You'll move through the world differently — more aligned, more together, more intentional about building the legacy you want for your family." },
    ],
    investment: { oneTime: "$2,697 one-time", monthly: "$499/month for 6 months" },
    future: [
      "You'll have couple friendships that actually understand.",
      "You'll laugh about things that used to feel heavy.",
      "You'll approach your marriage as a story you're building, not a problem to solve.",
      "You'll move through the world differently — more aligned, more together.",
    ],
    cta: "Apply for Vows & Vessels",
  },
];

const TESTIMONIALS = [
  { quote: "I came in guarded and performing. Six months later I have friendships where I can finally tell the truth — and a faith I actually practice on Tuesday mornings, not just Sundays.", name: "Rachel M.", context: "VESSELS · Spring 2026" },
  { quote: "We didn't fix our marriage in Vows & Vessels. We remembered why we chose it. The daily rhythms gave us language we didn't have before.", name: "Marcus & Tia J.", context: "Vows & Vessels · Spring 2026" },
  { quote: "The first Reflection Night wrecked me in the best way. I walked in a stranger and left feeling human again. It's why I applied for the cohort.", name: "Priya S.", context: "Reflection Night guest" },
];

const APP_DEADLINE = new Date("2026-11-08T23:59:59-08:00");
const daysUntil = (date) => Math.max(0, Math.ceil((date - new Date()) / 86400000));

function CountdownBadge({ dark = false }) {
  const days = daysUntil(APP_DEADLINE);
  if (days <= 0) return null;
  const urgent = days <= 14;
  return (
    <span data-testid="applications-countdown"
      className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs tracking-wide font-body ${dark ? "bg-[#E8B486]/15 text-[#E8B486] border border-[#E8B486]/30" : "bg-[#A9724E]/10 text-[#8a4f2c] border border-[#A9724E]/25"}`}>
      <CalendarDays className="w-3.5 h-3.5" strokeWidth={1.5} />
      Applications close in <strong className="font-semibold">{days} day{days === 1 ? "" : "s"}</strong>{urgent ? " — Nov 8" : " · Nov 8, 2026"}
    </span>
  );
}

export default function CohortsPage() {
  const { openApp } = useApp();
  useNoIndex();
  const [appsOpen, setAppsOpen] = useState(true);
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings`)
      .then((r) => r.json()).then((d) => setAppsOpen(!!d.cohort_applications_open)).catch(() => {});
  }, []);

  const CohortStory = ({ c, reverse }) => (
    <section id={c.id} className="py-16 md:py-24 scroll-mt-20">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className={`grid lg:grid-cols-2 gap-10 lg:gap-16 items-start ${reverse ? "lg:[&>*:first-child]:order-2" : ""}`}>
          <Reveal>
            <div className="overflow-hidden group lg:sticky lg:top-24">
              <img src={c.img} alt={c.title} className="w-full object-cover aspect-[4/5] transition-transform duration-[1400ms] group-hover:scale-105" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div>
              <div className="mb-4">
                <span data-testid={`audience-badge-${c.id}`} className="inline-block rounded-full bg-[#2A2825] text-[#E8B486] tracking-overline text-[10px] uppercase px-3 py-1.5">{c.audience}</span>
                <p className="tracking-overline text-xs uppercase text-[#A9724E] mt-2.5">{c.overline}</p>
              </div>
              <h2 className="font-serif-display font-light text-4xl md:text-5xl leading-tight mb-2">{c.title}</h2>
              <p className="font-serif-display text-xl md:text-2xl text-[#5E4B3C] italic mb-6">{c.subtitle}</p>
              <p className="text-[#5E4B3C] leading-relaxed font-body mb-10">{c.intro}</p>

              <p className="tracking-overline text-[11px] uppercase text-[#A9724E] mb-6">What this looks like</p>
              {c.lead && <div className="space-y-4 text-[#5E4B3C] leading-relaxed font-body mb-8">{c.lead.map((p, i) => <p key={i}>{p}</p>)}</div>}
              <div className="space-y-8 mb-10">
                {c.moments.map((m, i) => (
                  <div key={i} className="border-l-2 border-[#D8CEBE] pl-6">
                    <p className="font-serif-display text-2xl md:text-3xl text-[#2A2825] italic mb-2">{m.label}</p>
                    <p className="text-[#5E4B3C] leading-relaxed font-body">{m.body}</p>
                  </div>
                ))}
              </div>

              <div data-testid={`pricing-${c.id}`} className="border border-[#D8CFC0] bg-[#EAE3D9]/50 px-6 py-5 mb-8">
                <p className="tracking-overline text-[10px] uppercase text-[#A9724E] mb-2">Investment</p>
                <p className="font-serif-display text-2xl text-[#2A2825] leading-tight">{c.investment.oneTime}</p>
                <p className="font-serif-display italic text-[#8a7a6a] text-lg my-0.5">or</p>
                <p className="font-serif-display text-xl text-[#5E4B3C] leading-tight">{c.investment.monthly}</p>
                <p className="text-xs text-[#8a7a6a] mt-3 font-body">Scholarship / flexibility options available upon request. <Link to="/about#scholarships" className="text-[#A9724E] underline hover:text-[#925C3B]">Inquire about scholarships →</Link></p>
              </div>

              <div data-testid={`fit-${c.id}`} className="border border-[#D8CFC0] rounded-sm px-6 py-5 mb-8">
                <p className="tracking-overline text-[10px] uppercase text-[#A9724E] mb-3">Is this right for you?</p>
                <ul className="space-y-2 mb-4">
                  {c.fit.map((f, i) => (<li key={i} className="flex items-start gap-2.5 font-body text-[#2A2825] leading-snug"><Check className="w-4 h-4 text-[#5c8a5c] mt-0.5 shrink-0" strokeWidth={2} />{f}</li>))}
                </ul>
                <p className="tracking-overline text-[10px] uppercase text-[#8a7a6a] mb-2">This might not be for you if</p>
                <ul className="space-y-2">
                  {c.notFit.map((f, i) => (<li key={i} className="flex items-start gap-2.5 font-body text-[#8a7a6a] leading-snug"><X className="w-4 h-4 text-[#b18b7a] mt-0.5 shrink-0" strokeWidth={2} />{f}</li>))}
                </ul>
              </div>

              <div className="bg-[#2A2825] text-[#F4EFE6] p-6 md:p-8 mb-8 grain relative">
                <p className="tracking-overline text-[10px] uppercase text-[#C79A6E] mb-4">Six months from now</p>
                <ul className="space-y-3">
                  {c.future.map((f, i) => (<li key={i} className="flex items-start gap-3 font-body text-[#D9CFC2] leading-relaxed"><span className="text-[#E8B486] mt-1">✦</span>{f}</li>))}
                </ul>
              </div>

              {appsOpen && (
                <button data-testid={`apply-${c.track}-button`} onClick={() => openApp(c.track)} className="group inline-flex items-center gap-2 rounded-full bg-[#A9724E] hover:bg-[#925C3B] text-[#F4EFE6] px-7 py-3.5 text-sm tracking-wide transition-colors duration-500">
                  {c.cta} <ArrowRight className="w-4 h-4 transition-transform duration-500 group-hover:translate-x-1" strokeWidth={1.5} />
                </button>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );

  return (
    <div data-testid="cohorts-page" className="bg-[#F4EFE6] text-[#2A2825] overflow-x-hidden">
      {/* INTRO */}
      <section className="pt-28 md:pt-36 pb-4 scroll-mt-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <Reveal><p className="tracking-overline text-xs uppercase text-[#A9724E] mb-3">Applications required</p></Reveal>
          <Reveal delay={0.06}><h1 className="font-serif-display font-light text-4xl sm:text-5xl lg:text-6xl leading-tight">Outpoured Cohorts.</h1></Reveal>
          <Reveal delay={0.1}><p className="text-[#5E4B3C] font-body max-w-2xl mx-auto mt-5">In-person, ongoing communities that meet consistently over a season to practice together in real life — not another online course. Gather, receive challenges, Go &amp; Do, return, reflect, hold one another accountable, and repeat until practice begins becoming lifestyle.</p></Reveal>
        </div>
      </section>

      {COHORTS.map((c, i) => <CohortStory key={c.id} c={c} reverse={i % 2 === 1} />)}

      {/* APPLY */}
      {appsOpen && (
      <section data-testid="cohort-apply" className="py-14 md:py-20 bg-[#EAE3D9]/40 border-y border-[#D1C9BE]">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <Reveal><p className="tracking-overline text-xs uppercase text-[#A9724E] mb-4">The six-month cohort · Winter 2027</p></Reveal>
          <Reveal delay={0.06}><h2 className="font-serif-display font-light text-3xl sm:text-4xl lg:text-5xl leading-tight mb-6">This could be the six months you point back to forever.</h2></Reveal>
          <Reveal delay={0.1}><div className="mb-8 flex justify-center"><CountdownBadge /></div></Reveal>
          <Reveal delay={0.14}>
            <button data-testid="cohort-apply-button" onClick={() => openApp(null)} className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#A9724E] hover:bg-[#925C3B] text-[#F4EFE6] px-9 py-4 text-sm tracking-wide transition-colors duration-500">Apply for Winter 2027 <ArrowRight className="w-4 h-4 transition-transform duration-500 group-hover:translate-x-1" strokeWidth={1.5} /></button>
          </Reveal>
        </div>
      </section>
      )}

      {/* OFF-SEASON WAITLIST — notify when applications open */}
      {!appsOpen && (
      <section data-testid="cohort-waitlist" className="py-16 md:py-20 border-b border-[#D1C9BE]">
        <div className="max-w-2xl mx-auto px-6 md:px-12 text-center">
          <Reveal><p className="tracking-overline text-xs uppercase text-[#A9724E] mb-3">Between cohorts?</p></Reveal>
          <Reveal delay={0.06}><h2 className="font-serif-display font-light text-3xl md:text-4xl leading-tight mb-4">Get notified when applications open.</h2></Reveal>
          <Reveal delay={0.1}><p className="text-[#5E4B3C] font-body mb-8">Leave your email and you'll be the first to know the moment the next VESSELS and Vows &amp; Vessels cohorts open — before we announce anywhere else.</p></Reveal>
          <Reveal delay={0.14}>
            <div className="flex justify-center">
              <NotifyForm track="cohort-interest" testid="cohort-waitlist-form" buttonText="Notify me" successText="You're on the list — we'll reach out the moment applications open. ✦" placeholder="Email address" />
            </div>
          </Reveal>
        </div>
      </section>
      )}

      {/* WHY THE COHORTS CHANGE PEOPLE */}
      <section data-testid="cohort-highlights" className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <Reveal><p className="tracking-overline text-xs uppercase text-[#A9724E] mb-5">Why the cohorts change people</p></Reveal>
          <Reveal delay={0.06}><h2 className="font-serif-display font-light text-3xl sm:text-4xl lg:text-5xl leading-[1.08] mb-16 max-w-2xl">Small by design. Daily by rhythm. <span className="italic text-[#A9724E]">Embodied by practice.</span></h2></Reveal>
          <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
            {HIGHLIGHTS.map((h, i) => (
              <Reveal key={h.title} delay={0.1 + i * 0.08}>
                <div className="border-t border-[#D8CEBE] pt-6">
                  <h.icon className="w-6 h-6 text-[#A9724E] mb-5" strokeWidth={1.25} />
                  <h3 className="font-serif-display text-2xl text-[#2A2825] mb-3">{h.title}</h3>
                  <p className="text-[#5E4B3C] leading-relaxed font-body">{h.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section data-testid="testimonials" className="py-16 md:py-24 bg-[#EAE3D9]/40 border-y border-[#D1C9BE]">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <Reveal><p className="tracking-overline text-xs uppercase text-[#A9724E] mb-5">Stories from the table</p></Reveal>
          <Reveal delay={0.06}><h2 className="font-serif-display font-light text-3xl sm:text-4xl lg:text-5xl leading-[1.08] mb-16 max-w-2xl">What being poured out has looked like.</h2></Reveal>
          <div className="grid md:grid-cols-3 gap-8 lg:gap-10">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={t.name} delay={0.1 + i * 0.08}>
                <figure className="border-t border-[#D8CEBE] pt-6 h-full flex flex-col">
                  <blockquote className="font-serif-display text-xl md:text-2xl text-[#2A2825] italic leading-snug mb-6 flex-1">"{t.quote}"</blockquote>
                  <figcaption className="font-body"><p className="text-[#2A2825]">{t.name}</p><p className="tracking-overline text-[10px] uppercase text-[#A9724E] mt-1">{t.context}</p></figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
